import React from 'react';
import { FormikProps } from 'formik';
import {
    QuestionQuestionnaireElementType,
    StructureQuestionnaireElementType,
    AnswerableQuestionnaireElement,
    QuestionnaireElement
} from '../../../../api/jobOffers/jobOffers.dto';
import { Hr } from '@epicode/react-inspinia';
import { TFunction } from 'i18next';
import { renderQuestionnaireFileUpload } from './QuestionnaireFileUpload';
import { renderQuestionnaireAddress } from './QuestionnaireAddress';
import { renderQuestionnaireSingleLineText } from './QuestionnaireSingleLineText';
import uuid from 'uuid';
import { boundMethod } from 'autobind-decorator';
import { Api } from '../../../../api/_commons/http';
import { renderQuestionnaireHeader } from './QuestionnaireHeader';
import { renderQuestionnaireMultipleChoice } from './QuestionnaireMultipleChoice';
import { renderQuestionnaireMultiLineText } from './QuestionnaireMultiLineText';
import { renderQuestionnaireTextParagraph } from './QuestionnaireTextParagraph';
import { renderQuestionnaireSingleChoice } from './QuestionnaireSingleChoice';
import { renderQuestionnaireExperience } from './QuestionnaireExperience';
import { renderQuestionnaireEducation } from './QuestionnaireEducation';

export class OfferApplicationQuestionnaireRenderer<T> {
    private formikProps: FormikProps<T>;
    private t: TFunction;
    private jobOfferId: string;

    constructor(formikProps: FormikProps<T>, t: TFunction, jobOfferId: string) {
        this.formikProps = formikProps;
        this.t = t;
        this.jobOfferId = jobOfferId;
    }

    public setFormikProps(formikProps: FormikProps<T>) {
        this.formikProps = formikProps;
    }

    public renderQuestionnaireElements(elements: QuestionnaireElement[], formikProps: FormikProps<T>, t: TFunction) {
        this.formikProps = formikProps;
        this.t = t;

        return elements.map(this.renderQuestionnaireElement);
    }

    private renderQuestionnaireElement = (element: QuestionnaireElement) => {
        switch (element.$type) {
            case QuestionQuestionnaireElementType.QuestionnaireAddress: {
                return renderQuestionnaireAddress(this.formikProps)(
                    element,
                    this.addRequiredToLabelIfNeeded(this.t('jobApplication.address'), element),
                    this.t('jobApplication.addressPlaceholder')
                );
            }
            case QuestionQuestionnaireElementType.QuestionnaireAttachment: {
                return renderQuestionnaireFileUpload(this.formikProps, element, this.fileUploadHandler)(
                    this.addRequiredToLabelIfNeeded(element.question || '', element),
                    this.t('jobApplication.selectFile'),
                    this.t('jobApplication.attachmentPlaceholder'),
                    'application/pdf,.png,.jpg'
                );
            }
            case QuestionQuestionnaireElementType.QuestionnaireCoverLetter: {
                return renderQuestionnaireFileUpload(this.formikProps, element, this.fileUploadHandler)(
                    this.addRequiredToLabelIfNeeded(this.t('jobApplication.coverLetter'), element),
                    this.t('jobApplication.selectFile'),
                    this.t('jobApplication.coverLetterPlaceholder'),
                    'application/pdf'
                );
            }
            case QuestionQuestionnaireElementType.QuestionnaireEducation: {
                return renderQuestionnaireEducation(this.formikProps, element, this.t)();
            }
            case QuestionQuestionnaireElementType.QuestionnaireExperience: {
                return renderQuestionnaireExperience(this.formikProps, element, this.t)();
            }
            case QuestionQuestionnaireElementType.QuestionnaireHeader: {
                return renderQuestionnaireHeader()(element);
            }
            case QuestionQuestionnaireElementType.QuestionnaireMultipleChoice: {
                return renderQuestionnaireMultipleChoice(this.formikProps)(
                    element,
                    this.addRequiredToLabelIfNeeded(element.question || '', element)
                );
            }
            case QuestionQuestionnaireElementType.QuestionnaireMultiLineText: {
                return renderQuestionnaireMultiLineText(this.formikProps)(
                    element,
                    this.addRequiredToLabelIfNeeded(element.question || '', element)
                );
            }
            case QuestionQuestionnaireElementType.QuestionnairePhoto: {
                return renderQuestionnaireFileUpload(this.formikProps, element, this.fileUploadHandler)(
                    this.addRequiredToLabelIfNeeded(this.t('jobApplication.photo'), element),
                    this.t('jobApplication.selectFile'),
                    this.t('jobApplication.photoPlaceholder'),
                    'image/png,.jpg'
                );
            }
            case QuestionQuestionnaireElementType.QuestionnaireResume: {
                return renderQuestionnaireFileUpload(this.formikProps, element, this.fileUploadHandler)(
                    this.addRequiredToLabelIfNeeded(this.t('jobApplication.resume'), element),
                    this.t('jobApplication.selectFile'),
                    this.t('jobApplication.resumePlaceholder'),
                    'application/pdf'
                );
            }
            case QuestionQuestionnaireElementType.QuestionnaireSingleChoice: {
                return renderQuestionnaireSingleChoice(this.formikProps)(
                    element,
                    this.addRequiredToLabelIfNeeded(element.question || '', element)
                );
            }
            case QuestionQuestionnaireElementType.QuestionnaireSingleLineText: {
                return renderQuestionnaireSingleLineText(this.formikProps)(
                    element,
                    this.addRequiredToLabelIfNeeded(element.question || '', element)
                );
            }
            case QuestionQuestionnaireElementType.QuestionnaireSummary: {
                return renderQuestionnaireMultiLineText(this.formikProps)(
                    element,
                    this.addRequiredToLabelIfNeeded(this.t('jobApplication.summary'), element)
                );
            }
            case StructureQuestionnaireElementType.SectionBreak: {
                return <Hr type='solid' key={uuid.v4()} />;
            }
            case QuestionQuestionnaireElementType.QuestionnaireTextParagraph: {
                return renderQuestionnaireTextParagraph()(element);
            }
            default: {
                return null;
            }
        }
    };

    @boundMethod
    private async fileUploadHandler(file: File): Promise<string> {
        const uploadResult = await Api.uploadFile(file, {
            'epicode-ats-job-offer-id': this.jobOfferId,
            'epicode-ats-uploaded-filename': encodeURI(file.name)
        });

        if (uploadResult.status !== 200) {
            // TODO: show error somehow
            throw new Error(this.t('general.validation.invalidFile'));
        } else {
            return uploadResult.data ? uploadResult.data.fileIdentifier : '';
        }
    }

    private addRequiredToLabelIfNeeded = (label: string, element: AnswerableQuestionnaireElement) => {
        let result = label;

        if (element.isRequired) {
            result += ` ${this.t('general.requiredLabel')}`;
        }

        return result;
    };
}
